import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Fetchexecutequery } from "../../../services/process/BRS1/brsService7";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Calendar, FileText, Send, CheckCircle, Search, Download, Plus, Eye,
  Bell, ChevronDown, BarChart2, Settings, Building, LayoutDashboard,
  Globe, Target, AlertCircle, TrendingUp,
} from "lucide-react";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const T = {
  navy:     "#0a2352",
  navyMid:  "#143272",
  navyDark: "#061535",
  orange:   "#f97316",
  orangeL:  "#fb923c",
  bg:       "#f0f4fa",
  card:     "#ffffff",
  border:   "#dde4f0",
  text:     "#000000",
  muted:    "#000000",
  light:    "#f1f5f9",
  success:  "#16a34a",
  danger:   "#dc2626",
  warn:     "#d97706",
  purple:   "#7c3aed",
  accent:   "#2563eb",
  cyan:     "#0891b2",
};
const FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";
const MONTH_ORDER = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ─── Empty State ──────────────────────────────────────────────────────────────
const EMPTY = {
  profile: null,
  kpis: [],
  trend: [],
  funnel: [],
  eventsTable: [],
  activityFeed: [],
  alertFeed: [],
  deadlineStats: {          // ← add this
    regClosingSoon: 0,
    invitationReleasesSoon: 0,
    acceptanceDeadlinesSoon: 0,
    eventsStartingSoon: 0,
  },
};

const Ctx = React.createContext(EMPTY);
const useDash = () => useContext(Ctx);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt   = (n) => Number(n || 0).toLocaleString();
const pct   = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const monthOf = (v) => { const d = new Date(v); return isNaN(d) ? null : MONTH_ORDER[d.getMonth()]; };
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—";

const STATUS_META = {
  registration_open:    { label: "Reg. Open",   bg: "#d1fae5", tc: "#065f46" },
  invitations_released: { label: "Invites Out", bg: "#ede9fe", tc: "#5b21b6" },
  under_review:         { label: "Review",      bg: "#fef3c7", tc: "#92400e" },
  completed:            { label: "Done",        bg: "#dbeafe", tc: "#1d4ed8" },
  published:            { label: "Published",   bg: "#d1fae5", tc: "#065f46" },
};

// ─── Data Builder ─────────────────────────────────────────────────────────────
function build({ profile, events = [], applications = [], invitations = [] }) {
  const apps = applications.map(r => ({
    ...r,
    month: monthOf(r.applied_on),
  }));
  const invs = invitations.map(r => ({
    ...r,
    month: monthOf(r.invited_on),
  }));
  const evts = events.map(r => ({
    ...r,
    applications: +r.applications || 0,
    shortlisted:  +r.shortlisted  || 0,
    invitations:  +r.invitations  || 0,
    accepted:     +r.accepted     || 0,
  }));

  const totalApps  = apps.length;
  const totalShort = apps.filter(r => ["shortlisted","invited","accepted"].includes(r.application_status)).length;
  const totalInv   = invs.length;
  const totalAcc   = invs.filter(r => r.invitation_status === "accepted").length;
  const activeEvts = evts.filter(r => ["registration_open","published"].includes(r.event_status)).length;
  const fillRate   = totalApps ? pct(totalAcc, totalApps) : 0;
  const today = new Date();

const regClosingSoon = evts.filter(e => {
  if (!e.reg_close_date) return false;

  const diff =
    (new Date(e.reg_close_date) - today) /
    (1000 * 60 * 60 * 24);

  return diff >= 0 && diff <= 7;
}).length;

const invitationReleasesSoon = evts.filter(e => {
  if (!e.invitation_release_date) return false;

  const diff =
    (new Date(e.invitation_release_date) - today) /
    (1000 * 60 * 60 * 24);

  return diff >= 0 && diff <= 7;
}).length;

const acceptanceDeadlinesSoon = evts.filter(e => {
  if (!e.acceptance_deadline) return false;

  const diff =
    (new Date(e.acceptance_deadline) - today) /
    (1000 * 60 * 60 * 24);

  return diff >= 0 && diff <= 7;
}).length;

const eventsStartingSoon = evts.filter(e => {
  if (!e.event_from_date) return false;

  const diff =
    (new Date(e.event_from_date) - today) /
    (1000 * 60 * 60 * 24);

  return diff >= 0 && diff <= 14;
}).length;

  const trend = MONTH_ORDER.map(m => ({
    month:        m,
    applications: apps.filter(r => r.month === m).length,
    accepted:     invs.filter(r => r.month === m && r.invitation_status === "accepted").length,
  })).filter(r => r.applications || r.accepted);

  const eventsTable = evts
    .map(e => ({
      id:          e.primary_id,
      name:       e.event_name,
      apps:       e.applications,
      shortlisted:e.shortlisted,
      invited:    e.invitations,
      accepted:   e.accepted,
      fillRate:   e.applications ? pct(e.accepted, e.applications) : 0,
      status:     e.event_status,
    }))
    .sort((a, b) => b.apps - a.apps);

  const activityFeed = [
    ...apps.slice(0, 5).map(r => ({
      dot:   T.accent,
      actor: r.school_name || r.entity_name || "Unknown School",
      sub:   `Applied · ${r.event_name}`,
      time:  fmtDate(r.applied_on),
    })),
    ...invs.filter(r => r.invitation_status === "accepted").slice(0, 4).map(r => ({
      dot:   T.success,
      actor: r.school_name || r.entity_name || "Unknown School",
      sub:   `Accepted · ${r.event_name}`,
      time:  fmtDate(r.responded_on),
    })),
  ].slice(0, 8);

  const alertFeed = evts
    .filter(e => e.reg_close_date)
    .map(e => ({
      dot:   T.danger,
      actor: `Deadline: ${fmtDate(e.reg_close_date)}`,
      sub:   e.event_name,
      time:  "Upcoming",
    }))
    .slice(0, 5);

  return {
    profile,deadlineStats: {
  regClosingSoon,
  invitationReleasesSoon,
  acceptanceDeadlinesSoon,
  eventsStartingSoon
},
    kpis: [
      { label: "Active Events",  value: String(activeEvts),  sub: `of ${evts.length} total`,             icon: Calendar,     color: T.accent  },
      { label: "Applications",   value: fmt(totalApps),      sub: "total received",                      icon: FileText,     color: T.orange  },
      { label: "Invitations",    value: fmt(totalInv),       sub: "sent to schools",                     icon: Send,         color: T.cyan    },
      { label: "Fill Rate",      value: `${fillRate}%`,      sub: `${fmt(totalAcc)} accepted`,            icon: Target,       color: T.success },
    ],
    funnel: [
      { name: "Applied",    value: totalApps,  color: T.accent  },
      { name: "Shortlisted",value: totalShort, color: T.purple  },
      { name: "Invited",    value: totalInv,   color: T.cyan    },
      { name: "Accepted",   value: totalAcc,   color: T.success },
    ],
    trend,
    eventsTable,
    activityFeed,
    alertFeed,
  };
}
function UpcomingDeadlines() {
  const { deadlineStats } = useDash();

  return (
    <Card style={{ padding: 15}}>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: T.navy,
          marginBottom: 20
        }}
      >
        Upcoming Deadlines
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 16
        }}
      >
        <DeadlineCard
          label="Registration Closing"
          value={deadlineStats.regClosingSoon}
          color={T.danger}
        />

        <DeadlineCard
          label="Invitation Releases"
          value={deadlineStats.invitationReleasesSoon}
          color={T.accent}
        />

        <DeadlineCard
          label="Acceptance Deadlines"
          value={deadlineStats.acceptanceDeadlinesSoon}
          color={T.warn}
        />

        <DeadlineCard
          label="Events Starting"
          value={deadlineStats.eventsStartingSoon}
          color={T.success}
        />
      </div>
    </Card>
  );
}
function DeadlineCard({ label, value, color }) {
  return (
    <div
      style={{
        border: `1px solid ${color}30`,
        background: `${color}12`,
        borderRadius: 12,
        padding: 18
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: T.text
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 36,
          fontWeight: 800,
          color,
          marginTop: 8
        }}
      >
        {value}
      </div>
    </div>
  );
}
// ─── Primitives ───────────────────────────────────────────────────────────────
const Card = ({ children, style = {}, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: T.card,
      borderRadius: 12,
      border: `1px solid ${T.border}`,
      overflow: "hidden",
      fontFamily: FONT,
      ...style,
    }}
  >
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: (status || "").replace(/_/g, " "), bg: "#f3f4f6", tc: "#6b7280" };
  return (
    <span style={{
      background: m.bg, color: m.tc,
      padding: "2px 8px", borderRadius: 99,
      fontSize: 10, fontWeight: 600,
      letterSpacing: "0.04em", textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};

const ProgressBar = ({ value, color = T.accent, height = 4 }) => (
  <div style={{ height, background: T.light, borderRadius: 99, overflow: "hidden" }}>
    <div style={{ width: `${Math.min(value, 100)}%`, height: "100%", background: color, borderRadius: 99 }} />
  </div>
);

const Tab = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: active ? T.navy : "transparent",
      color: active ? "#fff" : T.muted,
      border: `1px solid ${active ? T.navy : T.border}`,
      borderRadius: 6, padding: "3px 10px",
      fontSize: 11, fontWeight: 500,
      cursor: "pointer", fontFamily: FONT,
    }}
  >
    {label}
  </button>
);

// ─── Left Nav ─────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { icon: LayoutDashboard, label: "Dashboard", active: true },
  { icon: Calendar,        label: "Events" },
  { icon: FileText,        label: "Applications" },
  { icon: Send,            label: "Invitations" },
  { icon: Building,        label: "Universities" },
  { icon: BarChart2,       label: "Analytics" },
  { icon: Settings,        label: "Settings" },
];

function LeftNav({ profile }) {
  return (
    <div style={{
      width: 60,
      background: `linear-gradient(180deg, ${T.navyDark} 0%, ${T.navyMid} 100%)`,
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "16px 0", gap: 4, flexShrink: 0,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, background: T.orange,
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
      }}>
        <Globe size={18} color="#fff" />
      </div>
      {NAV_ITEMS.map(({ icon: Icon, label, active }) => (
        <div
          key={label}
          title={label}
          style={{
            width: 44, height: 44, borderRadius: 10,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            background: active ? "rgba(255,255,255,.12)" : "transparent",
            border: active ? "1px solid rgba(255,255,255,.18)" : "1px solid transparent",
          }}
        >
          <Icon size={17} color={active ? "#fff" : "rgba(255,255,255,.45)"} />
        </div>
      ))}
      <div style={{
        marginTop: "auto",
        width: 34, height: 34, borderRadius: "50%",
        background: `linear-gradient(135deg, ${T.orange}, ${T.orangeL})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 900, fontSize: 13,
      }}>
        {(profile?.school_name || "S")[0].toUpperCase()}
      </div>
    </div>
  );
}

// ─── Top Bar ──────────────────────────────────────────────────────────────────
function TopBar({ profile, notifCount }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{
      height: 52, background: T.card,
      borderBottom: `1px solid ${T.border}`,
      display: "flex", alignItems: "center",
      padding: "0 20px", gap: 16, flexShrink: 0,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.navy, letterSpacing: -0.2 }}>
          School Event Management
        </div>
        <div style={{ fontSize: 11, color: T.muted }}>
          {time.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          {" · "}
          {time.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
      {/* Search */}
      <div style={{ position: "relative" }}>
        <Search size={12} color={T.muted} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)" }} />
        <input
          placeholder="Search events, schools…"
          style={{
            paddingLeft: 28, paddingRight: 12, paddingTop: 6, paddingBottom: 6,
            borderRadius: 8, border: `1.5px solid ${T.border}`,
            fontSize: 12, outline: "none", fontFamily: FONT, color: T.text,
            width: 220, background: T.bg,
          }}
        />
      </div>
      {/* Notifications */}
      <div style={{ position: "relative" }}>
        <button style={{
          width: 34, height: 34, background: T.bg,
          border: `1px solid ${T.border}`, borderRadius: 9,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Bell size={14} color={T.muted} />
        </button>
        {notifCount > 0 && (
          <div style={{
            position: "absolute", top: -3, right: -3,
            width: 16, height: 16, background: T.danger,
            borderRadius: "50%", fontSize: 9, fontWeight: 900,
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {notifCount}
          </div>
        )}
      </div>
      {/* Profile */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{
          width: 32, height: 32, borderRadius: "50%",
          background: `linear-gradient(135deg, ${T.orange}, ${T.orangeL})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 900, fontSize: 12,
        }}>
          {(profile?.school_name || "S")[0].toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{profile?.school_name || "School Admin"}</div>
          <div style={{ fontSize: 10, color: T.muted }}>Event Organizer</div>
        </div>
        <ChevronDown size={12} color={T.muted} />
      </div>
    </div>
  );
}

// ─── KPI Strip (4 cards) ──────────────────────────────────────────────────────
function KPIStrip() {
  const { kpis } = useDash();
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, flexShrink: 0 }}>
      {kpis.map(k => (
        <Card key={k.label} style={{ padding: "14px 16px", position: "relative" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: k.color }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ background: k.color + "18", borderRadius: 7, padding: 6, lineHeight: 0 }}>
              <k.icon size={14} color={k.color} />
            </div>
            <span style={{ fontSize: 15, color: T.muted, fontWeight: 500 }}>{k.label}</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: T.text, letterSpacing: "-0.04em", lineHeight: 1, marginBottom: 4 }}>
            {k.value}
          </div>
          <div style={{ fontSize: 15, color: T.muted }}>{k.sub}</div>
        </Card>
      ))}
    </div>
  );
}



// ─── Events Table ─────────────────────────────────────────────────────────────
function EventsTable() {
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 7;
  const { eventsTable } = useDash();
  const [q, setQ] = useState("");

  const filteredRows = eventsTable.filter(
    e => !q || e.name.toLowerCase().includes(q.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const rows = filteredRows.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <Card style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Header */}
      <div style={{
        padding: "10px 16px", borderBottom: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.navy, flex: 1 }}>Event Performance</span>
        <div style={{ position: "relative" }}>
          <Search size={11} color={T.muted} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)" }} />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Filter…"
            style={{
              paddingLeft: 26, paddingRight: 8, paddingTop: 5, paddingBottom: 5,
              borderRadius: 7, border: `1px solid ${T.border}`,
              fontSize: 11, outline: "none", fontFamily: FONT, width: 140, background: T.bg,
            }}
          />
        </div>
        <button style={{
          background: T.navy, border: "none", borderRadius: 7,
          padding: "5px 10px", color: "#fff", fontSize: 11, fontWeight: 700,
          fontFamily: FONT, cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
        }}>
          <Download size={10} /> Export
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
          <thead style={{ position: "sticky", top: 0 }}>
            <tr style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})` }}>
              {["Event", "Applications", "Shortlisted", "Invited", "Accepted", "Fill %", "Status"].map(h => (
                <th key={h} style={{
                  padding: "8px 12px", textAlign: "left",
                  fontSize: 15, fontWeight: 600, color: "rgb(255, 255, 255)",
                  textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((ev, i) => {
              const fc = ev.fillRate >= 70 ? T.success : T.warn;
              return (
                <tr
                  key={ev.id}
                  style={{
                    borderTop: `1px solid ${T.border}`,
                    background: i % 2 === 0 ? "#fff" : T.bg + "aa",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(10,35,82,.04)"}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : T.bg + "aa"}
                >
                  <td style={{ padding: "9px 12px", fontSize: 14, fontWeight: 500, color: T.navy, maxWidth: 200 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.name}</div>
                  </td>
                  <td style={{ padding: "9px 12px", fontSize: 14, color: T.text }}>{ev.apps}</td>
                  <td style={{ padding: "9px 12px", fontSize: 14, color: T.text }}>{ev.shortlisted}</td>
                  <td style={{ padding: "9px 12px", fontSize: 14, color: T.text }}>{ev.invited}</td>
                  <td style={{ padding: "9px 12px", fontSize: 14, color: T.text }}>{ev.accepted}</td>
                  <td style={{ padding: "9px 12px", minWidth: 90 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ flex: 1, height: 4, background: T.light, borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ width: `${ev.fillRate}%`, height: "100%", background: fc, borderRadius: 99 }} />
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: fc, minWidth: 28 }}>{ev.fillRate}%</span>
                    </div>
                  </td>
                  <td style={{ padding: "9px 12px" }}>
                    <StatusBadge status={ev.status} />
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: "24px", textAlign: "center", color: T.muted, fontSize: 12 }}>
                  No events found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{
        padding: "10px 16px", borderTop: `1px solid ${T.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexShrink: 0,
      }}>
        <div style={{ fontSize: 11, color: T.muted }}>
          Page {page} of {totalPages} &nbsp;·&nbsp; {filteredRows.length} event{filteredRows.length !== 1 ? "s" : ""}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            style={{
              padding: "5px 12px", borderRadius: 7, fontSize: 11, fontWeight: 600,
              cursor: page === 1 ? "not-allowed" : "pointer",
              background: page === 1 ? T.light : T.navy,
              color: page === 1 ? T.muted : "#fff",
              border: `1px solid ${page === 1 ? T.border : T.navy}`,
              fontFamily: FONT,
            }}
          >
            ← Prev
          </button>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
            style={{
              padding: "5px 12px", borderRadius: 7, fontSize: 11, fontWeight: 600,
              cursor: page === totalPages ? "not-allowed" : "pointer",
              background: page === totalPages ? T.light : T.navy,
              color: page === totalPages ? T.muted : "#fff",
              border: `1px solid ${page === totalPages ? T.border : T.navy}`,
              fontFamily: FONT,
            }}
          >
            Next →
          </button>
        </div>
      </div>
    </Card>
  );
}
// ─── Right Sidebar ────────────────────────────────────────────────────────────
function RightSidebar() {
  const { activityFeed, alertFeed } = useDash();

  const [tab, setTab] = useState("activity");
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 10;

  const feed =
    tab === "activity"
      ? activityFeed
      : alertFeed;

  const totalPages = Math.max(
    1,
    Math.ceil(feed.length / PAGE_SIZE)
  );

  const visibleFeed = feed.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [tab]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        height: "100%"
      }}
    >
      {/* Feed Header */}
      <Card
        style={{
          padding: "12px 14px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: T.navy
          }}
        >
          Live Feed
        </span>

        <div style={{ display: "flex", gap: 4 }}>
          <Tab
            label="Activity"
            active={tab === "activity"}
            onClick={() => setTab("activity")}
          />

          <Tab
            label="Alerts"
            active={tab === "alerts"}
            onClick={() => setTab("alerts")}
          />
        </div>
      </Card>

      {/* Feed List */}
      <Card
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "4px 0"
          }}
        >
          {visibleFeed.length === 0 && (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: T.muted,
                fontSize: 13
              }}
            >
              No items found
            </div>
          )}

          {visibleFeed.map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 12,
                padding: "12px 14px",
                borderBottom: `1px solid ${T.light}`,
                alignItems: "flex-start",
                cursor: "pointer"
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = T.bg)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  "transparent")
              }
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: item.dot,
                  flexShrink: 0,
                  marginTop: 5
                }}
              />

              <div
                style={{
                  flex: 1,
                  overflow: "hidden"
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.navy,
                    marginBottom: 2
                  }}
                >
                  {item.actor}
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: T.muted,
                    lineHeight: 1.5
                  }}
                >
                  {item.sub}
                </div>
              </div>

              <div
                style={{
                  fontSize: 11,
                  color: T.muted,
                  flexShrink: 0
                }}
              >
                {item.time}
              </div>
            </div>
          ))}
        </div>

        {/* Feed Pagination */}
        <div
          style={{
            borderTop: `1px solid ${T.border}`,
            padding: "10px 12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: T.muted
            }}
          >
            Page {page} of {totalPages}
          </div>

          <div
            style={{
              display: "flex",
              gap: 6
            }}
          >
            <button
              disabled={page === 1}
              onClick={() =>
                setPage((p) => Math.max(1, p - 1))
              }
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                border: `1px solid ${T.border}`,
                background:
                  page === 1
                    ? T.light
                    : T.navy,
                color:
                  page === 1
                    ? T.muted
                    : "#fff",
                cursor:
                  page === 1
                    ? "not-allowed"
                    : "pointer",
                fontSize: 12,
                fontWeight: 600
              }}
            >
              Prev
            </button>

            <button
              disabled={page === totalPages}
              onClick={() =>
                setPage((p) =>
                  Math.min(totalPages, p + 1)
                )
              }
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                border: `1px solid ${T.border}`,
                background:
                  page === totalPages
                    ? T.light
                    : T.navy,
                color:
                  page === totalPages
                    ? T.muted
                    : "#fff",
                cursor:
                  page === totalPages
                    ? "not-allowed"
                    : "pointer",
                fontSize: 12,
                fontWeight: 600
              }}
            >
              Next
            </button>
          </div>
        </div>
      </Card>


    </div>
  );
}
// ─── Root ─────────────────────────────────────────────────────────────────────
export default function EventCreatorDashboard() {
  const uc = JSON.parse(sessionStorage.getItem("userCredentials") || "{}");
  const [data, setData]       = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const creds    = Fetchexecutequery();
        const query    = async (sql) => (await axios.post(creds[1], { query: sql }, creds[0])).data || [];
        const entityId = uc.selectedentity || uc.entities_id;
        const tenantId = uc.tenants_id;

        const [profileRows, eventRows, applicationRows, invitationRows] = await Promise.all([
          query(`
            SELECT school_name, city, country, school_type
            FROM edu_platform.school_profile
            WHERE user_id = ${uc.user_id} AND is_active = 1
            ORDER BY primary_id DESC LIMIT 1
          `),
          query(`
            SELECT e.primary_id, e.event_name, e.event_category, e.event_status,
                   e.event_from_date, e.event_to_date, e.reg_close_date,
                   e.invitation_release_date, e.acceptance_deadline,
                   (SELECT COUNT(*) FROM edu_platform.event_applications ea WHERE ea.event_id = e.primary_id AND ea.is_active = 1) AS applications,
                   (SELECT COUNT(*) FROM edu_platform.event_applications ea WHERE ea.event_id = e.primary_id AND ea.application_status = 'shortlisted' AND ea.is_active = 1) AS shortlisted,
                   (SELECT COUNT(*) FROM edu_platform.event_invitations ei WHERE ei.event_id = e.primary_id AND ei.is_active = 1) AS invitations,
                   (SELECT COUNT(*) FROM edu_platform.event_invitations ei WHERE ei.event_id = e.primary_id AND ei.invitation_status = 'accepted' AND ei.is_active = 1) AS accepted
            FROM edu_platform.events e
            WHERE e.is_active = 1 AND e.creator_entity_id = ${entityId} AND e.tenant_id = ${tenantId}
            ORDER BY e.primary_id DESC
          `),
          query(`
            SELECT ea.primary_id, ea.event_id, ea.application_status, ea.invitation_status,
                   ea.applied_on, ea.reviewed_on, ea.invited_on, ea.responded_on,
                   e.event_name, sp.school_name, sp.country
            FROM edu_platform.event_applications ea
            JOIN edu_platform.events e ON e.primary_id = ea.event_id
            LEFT JOIN edu_platform.school_profile sp ON sp.entity_id = ea.applicant_entity_id AND sp.is_active = 1
            WHERE ea.is_active = 1 AND e.creator_entity_id = ${entityId} AND e.tenant_id = ${tenantId}
            ORDER BY ea.applied_on DESC
          `),
          query(`
            SELECT ei.primary_id, ei.event_id, ei.invitation_status,
                   ei.invited_on, ei.responded_on, ei.acceptance_deadline,
                   e.event_name, sp.school_name, sp.country
            FROM edu_platform.event_invitations ei
            JOIN edu_platform.events e ON e.primary_id = ei.event_id
            LEFT JOIN edu_platform.school_profile sp ON sp.entity_id = ei.applicant_entity_id AND sp.is_active = 1
            WHERE ei.is_active = 1 AND e.creator_entity_id = ${entityId} AND e.tenant_id = ${tenantId}
            ORDER BY ei.invited_on DESC
          `),
        ]);

        setData(build({
          profile:      profileRows[0] || { school_name: uc.display_name || "School Admin" },
          events:       eventRows,
          applications: applicationRows,
          invitations:  invitationRows,
        }));
      } catch (err) {
        console.error("Dashboard load failed:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Ctx.Provider value={data}>
      {/*
        Ensure parent sets:
          body, #root { margin: 0; padding: 0; height: 100vh; overflow: hidden; }
      */}
      <div style={{
        display: "flex", overflow: "hidden",
        background: T.bg, fontFamily: FONT,
        opacity: loading ? 0.8 : 1, transition: "opacity .3s",
      }}>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>

          {/* Content */}
          <div style={{
            flex: 1, overflow: "hidden",
            padding: "14px",
            display: "grid",
            gridTemplateColumns: "1fr 240px",
            gap: 12,
            minHeight: 0,
          }}>
            {/* Left main column */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 0, overflow: "hidden" }}>

              {/* Row 1: KPI Cards */}
              <KPIStrip />

              <UpcomingDeadlines />

              {/* Row 3: Events Table */}
              <div style={{  display: "flex", flexDirection: "column" }}>
                <EventsTable />
              </div>
            </div>

            {/* Right sidebar */}
            <div style={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
              <RightSidebar />
            </div>
          </div>
        </div>
      </div>
    </Ctx.Provider>
  );
}